import { useCallback } from 'react'
import { SupportedLocaleEnum } from '@reward-platform/platform-config'
import { createPathname } from '@reward-platform/utils'
import { usePlatformConfig } from './PlatformConfigContext'

const INTERNAL_PATH_REGEX = /^\/([a-zA-Z0-9\-._~:@%&?=/]*)$/

type RouteFunctions = {
  /**
   * uses basePath from platform context to conditionally create pathname used for routing
   */
  createRoutePath(pathname: string, locale?: string): string
}

/** Preserve the existing pathname/base-path/locale behavior for both route hooks. */
const buildRoutePath = (pathname: string, basePath?: string, locale?: string): string => {
  const isInternalPath = INTERNAL_PATH_REGEX.test(pathname)
  if (!!pathname && !isInternalPath) {
    return pathname
  }
  const localeParse = SupportedLocaleEnum.safeParse(locale)
  if (localeParse.success) {
    return createPathname(pathname, basePath, locale)
  }
  return createPathname(pathname, basePath)
}

/**
 * Hook to determine application routes depending on platform base path
 * used for routing in avios.com
 */
export const usePlatformRoutes = (): RouteFunctions => {
  const { basePath } = usePlatformConfig()

  const createRoutePath: RouteFunctions['createRoutePath'] = (pathname, locale) =>
    buildRoutePath(pathname, basePath, locale)

  return { createRoutePath }
}

/** Opt in where function identity is used to cache lists; keep legacy effect consumers unchanged. */
export const useStableCreateRoutePath = (): RouteFunctions['createRoutePath'] => {
  const { basePath } = usePlatformConfig()
  return useCallback((pathname, locale) => buildRoutePath(pathname, basePath, locale), [basePath])
}
